import React, { useState, useEffect, useMemo, useRef } from 'react'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import { useConfig } from '../ConfigProvider'
import { useForm } from '../Form/context'
import { useInputGroup } from '../InputGroup/context'
import { CONTROL_SIZES, SIZES } from '../utils/constant'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import get from 'lodash/get'

const InputModern = React.forwardRef((props, ref) => {
    const {
        asElement: Component,
        className,
        disabled,
        invalid,
        prefix,
        size,
        suffix,
        textArea,
        type,
        style,
        unstyle,
        field,
        form,
        label,
        darkBackground,
        fieldsetClassName,
        ...rest
    } = props

    const [prefixGutter, setPrefixGutter] = useState(0)
    const [suffixGutter, setSuffixGutter] = useState(0)

    const { themeColor, controlSize, primaryColorLevel, direction } =
        useConfig()
    const formControlSize = useForm()?.size
    const inputGroupSize = useInputGroup()?.size

    const inputSize = size || inputGroupSize || formControlSize || controlSize

    const fixControlledValue = (val) => {
        if (typeof val === 'undefined' || val === null) {
            return ''
        }
        return val
    }

    if ('value' in props) {
        rest.value = fixControlledValue(props.value)
        delete rest.defaultValue
    }

    const isInvalid = useMemo(() => {
        let validate = false
        if (!isEmpty(form)) {
            const { touched, errors } = form
            const touchedField = get(touched, field.name)
            const errorField = get(errors, field.name)
            validate = touchedField && errorField
        }
        if (typeof invalid === 'boolean') {
            validate = invalid
        }
        return validate
    }, [form, invalid, field])

    const inputDefaultClass = 'input'
    const inputSizeClass = `input-${inputSize} h-${CONTROL_SIZES[inputSize]}`
    const inputFocusClass = `focus:ring-${themeColor}-${primaryColorLevel} focus-within:ring-${themeColor}-${primaryColorLevel} focus-within:border-${themeColor}-${primaryColorLevel} focus:border-${themeColor}-${primaryColorLevel}`
    const inputWrapperClass = `input-wrapper ${
        prefix || suffix ? className : ''
    }`
    const inputClass = classNames(
        inputDefaultClass,
        !textArea && inputSizeClass,
        !isInvalid && inputFocusClass,
        !prefix && !suffix ? className : className,
        disabled && 'input-disabled',
        isInvalid && 'input-invalid',
        textArea && 'input-textarea',
        'bg-transparent !p-2.5 !border-0 focus:!ring-0',
        label && '!p-4 !pt-2.5', 
        darkBackground && '!text-white'
    )

    const prefixNode = useRef()
    const suffixNode = useRef()

    const getAffixSize = () => {
        if (!prefixNode.current && !suffixNode.current) {
            return
        }
        const prefixNodeWidth = prefixNode?.current?.offsetWidth
        const suffixNodeWidth = suffixNode?.current?.offsetWidth

        if (isNil(prefixNodeWidth) && isNil(suffixNodeWidth)) {
            return
        }

        if (prefixNodeWidth) {
            setPrefixGutter(prefixNodeWidth)
        }

        if (suffixNodeWidth) {
            setSuffixGutter(suffixNodeWidth)
        }
    }

    useEffect(() => {
        getAffixSize()
    }, [prefix, suffix])

    const remToPxConvertion = (pixel) => 0.0625 * pixel

    const affixGutterStyle = () => {
        const leftGutter = `${remToPxConvertion(prefixGutter) + 1}rem`
        const rightGutter = `${remToPxConvertion(suffixGutter) + 1}rem`
        let gutterStyle = {}

        if (direction === 'ltr') {
            if (prefix) {
                gutterStyle.paddingLeft = leftGutter
            }

            if (suffix) {
                gutterStyle.paddingRight = rightGutter
            }
        }

        if (direction === 'rtl') {
            if (prefix) {
                gutterStyle.paddingRight = leftGutter
            }

            if (suffix) {
                gutterStyle.paddingLeft = rightGutter
            }
        }

        return gutterStyle
    }

    const inputProps = {
        className: `h-full ${!unstyle ? inputClass : ''}`,
        disabled,
        type,
        ref,
        ...field,
        ...rest,
    }

    const renderTextArea = (
        <fieldset className={`relative w-full rounded-2xl ${!label ? `bg-tertiary-100 dark:bg-gray-700` : `border border-tertiary-500/20 dark:border-gray-600`} outline-none transition focus-within:!border-primary-600 transition-all ${suffix ? `pr-10` : ``} ${prefix ? `!pl-10` : ``} ${fieldsetClassName}`}>
            {label && <legend className={`text-xs ml-6 lg:text-xs px-2 font-semibold text-tertiary/20 dark:text-gray-400 capitalize dark:text-white ${darkBackground ? `!text-tertiary-100` : ``}`}>{label}</legend>}
            {prefix && <span className='absolute left-4 top-[50%] translate-y-[-50%] text-2xl'>{prefix}</span>}
            <textarea
                style={{ ...style }}
                {...inputProps}
            />
            {suffix && <span className='absolute right-4 top-[50%] translate-y-[-63%] text-2xl'>{suffix}</span>}
        </fieldset>
    )

    const renderInput = (
        <fieldset className={`relative w-full rounded-2xl ${!label ? `bg-gray-100 dark:bg-gray-700` : `border border-gray/20 dark:border-gray-600`} outline-none transition focus-within:!border-primary-600 transition-all ${suffix ? `pr-10` : ``} ${prefix ? `!pl-10` : ``} ${fieldsetClassName}`}>
            {label && <legend className={`text-xs ml-6 lg:text-xs px-2 font-semibold text-tertiary-500 dark:text-gray-400/20 dark:text-gray-400 capitalize dark:text-white ${darkBackground ? `!text-tertiary-100` : ``}`}>{label}</legend>}
            {prefix && <span className='absolute left-4 top-[50%] translate-y-[-50%] text-2xl'>{prefix}</span>}
            <Component
                style={{ ...affixGutterStyle(), ...style }}
                {...inputProps}
            />
            {suffix && <span className='absolute right-4 top-[50%] translate-y-[-63%] text-2xl'>{suffix}</span>}
        </fieldset>
    )

    const renderAffixInput = (
        <span className={inputWrapperClass}>
            {prefix ? (
                <div
                    className="input-suffix-start"
                    ref={(node) => (prefixNode.current = node)}
                >
                    {' '}
                    {prefix}{' '}
                </div>
            ) : null}
            {renderInput}
            {suffix ? (
                <div
                    className="input-suffix-end"
                    ref={(node) => (suffixNode.current = node)}
                >
                    {suffix}
                </div>
            ) : null}
        </span>
    )

    const renderChildren = () => {
        if (textArea) {
            return renderTextArea
        }

        return renderInput;
    }

    return renderChildren()
})

InputModern.propTypes = {
    asElement: PropTypes.string,
    type: PropTypes.string,
    className: PropTypes.string,
    size: PropTypes.oneOf([SIZES.LG, SIZES.SM, SIZES.MD]),
    value: PropTypes.any,
    invalid: PropTypes.bool,
    suffix: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    prefix: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    unstyle: PropTypes.bool,
}

InputModern.defaultProps = {
    type: 'text',
    asElement: 'input',
    className: '',
    unstyle: false,
}

export default InputModern
